<template>
  <b-sidebar
    id="sidebar-old-create-apply-document"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ applyDocument.id ? $t('Update') : $t('Create') }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />

      </div>
      <!-- Body -->
      <div
        v-if="!applyDocument.id"
        class="p-1"
      >
        <b-form-group
          :label="$t('Passport series and number')"
          label-for="Passport_series_and_number"
        >
          <b-form-input
            id="Passport_series_and_number"
            v-model="passport_number"
            :placeholder="$t('Passport series and number')"
          />
        </b-form-group>
        <b-form-group
          :label="$t('Birthday')"
          label-for="birthday"
        >
          <flat-pickr
            id="birthday"
            v-model="birth_date"
            class="form-control"
            :config="{ dateFormat: 'Y-m-d'}"
            placeholder="YYYY-MM-DD"
          />
        </b-form-group>
        <div
          v-if="error"
          class="text-danger mt-1"
        >
          {{ error }}
        </div>

        <b-button
          v-if="!searching"
          variant="primary"
          @click="searchDoc"
        >
          {{ $t('Search here') }}
        </b-button>
        <b-button
          v-else
          disabled
          variant="primary"
        >
          <b-spinner
            small
            type="border"
          />
        </b-button>
      </div>

      <b-form
        v-if="applyDocument.user"
        class="p-2"
        @submit.prevent
      >
        <div class="mb-1">
          <strong>{{ applyDocument.user.full_name }} </strong>
        </div>

        <validation-observer ref="applyDocumentCreate">
          <!-- Dc Type -->
          <b-form-group
            :label="$t('Dc Type')"
            label-for="dc_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dc Type')"
              rules="required"
            >
              <v-select
                id="dc_type"
                v-model="applyDocument.dc_type"
                :reduce="(option) => option.id"
                :options="dcTypes"
                :get-option-label="(option) => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Status-->
          <b-form-group
            :label="$t('Status')"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Status')"
              rules="required"
            >
              <v-select
                v-model="applyDocument.status"
                :reduce="(option) => option.id"
                :options="statuses"
                :get-option-label="(option) => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Scientific work name -->
          <b-form-group
            :label="$t('Scientific work name')"
            label-for="scientific_work_name"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Scientific work name')"
              rules="required"
            >
              <b-form-input
                id="scientific_work_name"
                v-model="applyDocument.scientific_work_name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Quarter -->
          <validation-provider
            #default="{ errors }"
            :name="$t('Quarter')"
            rules="required"
          >
            <b-form-group
              :label="$t('Quarter')"
              label-for="quarter"
            >
              <v-select
                id="quarter"
                v-model="applyDocument.quarter_id"
                :reduce="(option) => option.id"
                :options="quarters"
                :get-option-label="(option) => $t('{year}-year, {quarter}-quarter', { year: option.period.admission_year, quarter: option.quarter})"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Edu Lang -->
          <b-form-group
            :label="$t('Education Language')"
            label-for="edu_lang"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Education Language')"
              rules="required"
            >
              <v-select
                id="edu_lang"
                v-model="applyDocument.edu_lang"
                :reduce="(option) => option.id"
                :options="eduLangTypes"
                :get-option-label="(option) => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Course')"
            label-for="course"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Course')"
              rules="required"
            >
              <v-select
                id="course"
                v-model="applyDocument.course"
                :options="[1,2,3]"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Directions -->
          <b-form-group
            :label="$t('Directions')"
            label-for="direction"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Directions')"
              rules="required"
            >
              <v-select
                id="direction"
                v-model="applyDocument.direction_id"
                :reduce="(option) => option.direction.id"
                :options="directions"
                :get-option-label="(option) => option.direction ? option.direction.code + ' - ' + $langName(option.direction.name): null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('File')"
            label-for="file"
          >
            <div
              v-if="applyDocument.old_apply_doc_file"
              class="input-group"
            >
              <div class="input-group-prepend">
                <span class="input-group-text border-success">
                  <b-link
                    :href="applyDocument.old_apply_doc_file"
                    download="file"
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="cursor-pointer"
                      size="16"
                    />
                  </b-link>

                </span>
              </div>
              <div
                class="form-control border-success"
              >
                {{ $t('File') }}
              </div>
              <div
                class="input-group-append"
              >
                <span class="input-group-text">
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer danger"
                    size="16"
                    @click="cancel"
                  />
                </span>
              </div>
            </div>
            <validation-provider
              v-else
              #default="{ errors }"
              :name="$t('File')"
              rules="required|file-size:10"
            >
              <b-form-file
                v-model="file"
                :placeholder="$t('Choose a file or drop it here...')"
                accept="application/pdf"
                :drop-placeholder="$t('Drop file here...')"
                :state="errors.length > 0 ? false:null"
                @change="handleFile"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Research Advisor')"
            label-for="advisor"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Research Advisor')"
            >
              <v-select
                v-model="applyDocument.advisor_id"
                :options="advisorList"
                :reduce="(option) => option.id"
                :get-option-label="(option) => option.full_name"
                @search="fetchAdvisor"
                @input="inputAdvisor"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
        <!-- Form Actions -->
        <div class="d-flex mt-2">
          <b-button
            variant="primary"
            class="mr-2"
            type="submit"
            @click="submit(hide)"
          >
            {{ applyDocument.id ? $t('Update') : $t('Create') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="clear(hide)"
          >
            {{ $t('Cancel') }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BSpinner, BFormFile, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import { onUnmounted } from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import { required } from '@/libs/validations/validations'
import permission from './permission'
import store from '@/store'
import { myOrganization } from '@/auth/utils'
import researchAdvisorModule from '@/store/research-advisor'

export default {
  components: {
    BLink,
    BFormFile,
    BFormInput,
    ValidationProvider,
    ValidationObserver,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    vSelect,
    BSpinner,
    flatPickr,
  },
  data() {
    return {
      required,
      passport_number: '',
      birth_date: '',
      searching: false,
      error: '',
      base64: null,
      file: null,
      advisors: [],
    }
  },
  computed: {
    applyDocument: {
      get() {
        return this.$store.state.applyDocument.applyDocument
      },
      set(data) {
        this.$store.commit('applyDocument/UPDATE', data)
      },
    },
    dcTypes() {
      return this.$store.state.staticModule.dcTypes
    },

    directions() {
      return this.$store.state.applyDocument.directions
    },

    quarters() {
      return this.$store.state.staticModule.quotes
    },
    eduLangTypes() {
      return this.$store.state.staticModule.eduLangTypes
    },
    advisorList() {
      if (this.selectedAdvisor && !this.advisors.find(x => x.id === this.selectedAdvisor.id)) {
        return [this.selectedAdvisor, ...this.advisors]
      }
      return this.advisors
    },
  },
  mounted() {
    localize(this.$i18n.locale)
    this.loadData()
    this.$store.dispatch('staticModule/dcType')
    this.$store.dispatch('applyDocument/directions', { org: myOrganization().id, limit: 9999 })
  },
  methods: {
    handleFile(e) {
      // eslint-disable-next-line prefer-destructuring
      this.base64 = e.target.files[0]
    },
    searchDoc() {
      this.error = ''

      // if (!/^[A-Z]{2}\d{7}$/.test(this.passport_number)) {
      //   this.error = 'Passport raqami AA1234567 formatda bo‘lishi kerak'
      //   return
      // }

      if (!this.birth_date) {
        this.error = 'Tug‘ilgan sana tanlanmagan'
        return
      }

      this.searching = true

      this.$store
        .dispatch('applyDocument/filterByPassport', { date: this.birth_date, passport_number: this.passport_number })
        .then(({ data }) => {
          if (data) {
            this.applyDocument.user = data
            this.applyDocument.user_id = data?.user_id
          } else {
            this.applyDocument.user = null
            this.applyDocument.user_id = null
            this.error = 'Foydalanuvchi topilmadi'
          }
        })
        .catch(() => {
          this.applyDocument.user = null
          this.applyDocument.user_id = null
          this.error = 'Foydalanuvchi topilmadi'
        })
        .finally(() => {
          this.searching = false
        })
    },
    loadData() {
      if (this.applyDocument.advisor) {
        this.selectedAdvisor = this.applyDocument.advisor
      }
      this.fetchAdvisor('', () => {})
    },
    clear(hide) {
      this.passport_number = ''
      this.birth_date = ''
      this.error = ''
      this.file = null
      this.base64 = null
      this.$store.commit('applyDocument/FRESH')
      this.$refs.applyDocumentCreate.reset()
      hide()
    },
    submit(hide) {
      this.$refs.applyDocumentCreate.validate().then(success => {
        const res = { ...this.applyDocument }
        if (success) {
          if (this.base64) {
            res.old_apply_doc_file = this.base64
          } else {
            delete res.old_apply_doc_file
          }
          this.$createSwal(this, res, `applyDocument/${res.id ? 'oldUpdate' : 'oldCreate'}`, () => {
            this.$emit('refetchData')
            this.$store.commit('applyDocument/FRESH')
          })
          hide()
        }
      })
    },
    fetchAdvisor(search, loading) {
      loading(true)
      this.$store.dispatch('staticModule/researchAdvisors', { search }).then(res => {
        this.advisors = res
        loading(false)
      })
    },
    inputAdvisor(id) {
      this.selectedAdvisor = this.advisors.find(x => x.id === id)
    },
    cancel() {
      this.applyDocument.old_apply_doc_file = null
      this.file = null
    },
  },
  setup() {
    const MODULE_NAME = 'researchAdvisor'
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, researchAdvisorModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    const {
      applyDocumentAdmittedToStudy, applyDocumentHasGraduated, applyDocumentHasBeenCanceled, applyDocumentSuccessfullyDefended,
    } = store.state.staticModule
    const statuses = store.state.staticModule.applyDocumentStatuses.filter(status => [applyDocumentAdmittedToStudy, applyDocumentHasGraduated, applyDocumentHasBeenCanceled, applyDocumentSuccessfullyDefended].includes(status.id))
    return {
      statuses,
      ...permission(),
    }
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
